/**
 * 对账页的时间区间(纯逻辑,可单测)。
 *
 * 后端的 `since` / `until` 必填且是 ISO 时间串;界面上是两个 `datetime-local` 输入框,值是**本地时间**串
 * (`YYYY-MM-DDTHH:mm`,不带时区)。这个文件就管这一件事:本地时间串 ⇄ ISO 时间串,以及区间本身合不合法。
 *
 * **区间非法(空、写反、写错)一律抛错,不兜一个默认区间继续查** —— 兜了的话,人以为自己在看 7 月的账,
 * 实际看的是后端替他挑的另一段。
 */

/**
 * 快捷区间。点一下就把 `since` / `until` 写成「到现在为止的这么长一段」—— 页面上不需要一句话解释
 * 「最近 24 小时」是什么意思,点它就看得见两个输入框变了。
 */
export const RECON_RANGES = [
  { value: '24h', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
]

/** 打开页面时选中的快捷区间。 */
export const DEFAULT_RECON_RANGE = '24h'

/**
 * 快捷区间 → 两个 `datetime-local` 的值。未知区间直接抛,不悄悄退回 24 小时。
 *
 * @param {Date} now
 * @param {string} range
 * @returns {{ since: string, until: string }}  本地时间串
 */
export function reconWindowOf(now, range) {
  const picked = RECON_RANGES.find((candidate) => candidate.value === range)
  if (!picked) throw new Error(`对账:未知的快捷区间 ${range}`)
  return {
    since: toLocalInput(new Date(now.getTime() - picked.ms)),
    until: toLocalInput(now),
  }
}

/**
 * @param {Date} now
 * @returns {{ since: string, until: string }}  两个 `datetime-local` 的值(本地时间串)
 */
export function defaultReconWindow(now) {
  return reconWindowOf(now, DEFAULT_RECON_RANGE)
}

/**
 * 界面上的区间 → 后端的 query。
 *
 * @param {{ since: string, until: string, limit: number, cursor: string|null }} window
 * @returns {{ since: string, until: string, limit: number, cursor: string|null }}
 */
export function reconQuery({ since, until, limit, cursor }) {
  const from = toInstant(since, 'since')
  const to = toInstant(until, 'until')
  if (from.getTime() >= to.getTime()) {
    throw new Error(`对账区间非法:开始时间 ${since} 不早于结束时间 ${until}`)
  }
  return { since: from.toISOString(), until: to.toISOString(), limit, cursor }
}

/**
 * `datetime-local` 的值(本地时间)→ 时刻。
 * @param {string} localValue
 * @param {string} field
 * @returns {Date}
 */
function toInstant(localValue, field) {
  const at = new Date(localValue)
  if (localValue === '' || Number.isNaN(at.getTime())) {
    throw new Error(`对账区间非法:${field} 不是可解析的时间(${localValue || '空'})`)
  }
  return at
}

/**
 * 时刻 → `datetime-local` 的值。`toISOString` 是 UTC,直接塞进输入框会把人看到的时间偏掉时区,
 * 所以按本地各字段拼。
 * @param {Date} at
 * @returns {string}
 */
function toLocalInput(at) {
  const pad = (n) => String(n).padStart(2, '0')
  const date = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
  return `${date}T${pad(at.getHours())}:${pad(at.getMinutes())}`
}
